import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import messages from './messages';
import newTenantEventsMessages from '../NewTenantEvents/messages';
import { processTableFilterOptions } from '../../../utils/table-helpers';
import { DEFAULT_APPROVED_AMOUNT_COLUMN_WIDTH } from '../constants';

function useBuildFilterOptions({ results }) {
  return useMemo(() => {
    const options = {
      bedsBaths: [],
      incomeCode: [],
      turnoverCode: [],
      changeCode: [],
    };

    if (!results) {
      return options;
    }

    results.reduce((acc, result) => {
      if (result.bedsBaths) acc.bedsBaths.push(result.bedsBaths);
      if (result.incomeCode) acc.incomeCode.push(result.incomeCode);
      if (result.turnoverCode) acc.turnoverCode.push(result.turnoverCode);
      if (result.changeCode) acc.changeCode.push(result.changeCode);
      return acc;
    }, options);

    // Remove duplicates, sort, and map to label/value objects
    processTableFilterOptions(options);

    return options;
  }, [results]);
}

const useHeaders = ({ results }) => {
  const filterOptions = useBuildFilterOptions({ results });

  return useMemo(
    () => [
      {
        id: 'headOfHousehold',
        label: <FormattedMessage {...messages.headOfHousehold} />,
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'unit',
        label: <FormattedMessage {...messages.unit} />,
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'hudUnitNumber',
        label: <FormattedMessage {...newTenantEventsMessages.hudUnitNumber} />,
        info: (
          <FormattedMessage {...newTenantEventsMessages.hudUnitNumberTooltip} />
        ),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'bedsBaths',
        label: <FormattedMessage {...messages.bedsBaths} />,
        dataType: 'string',
        sortable: true,
        searchable: true,
        multiselect: true,
        filterOptions: filterOptions.bedsBaths,
      },
      {
        id: 'contractRent',
        label: <FormattedMessage {...messages.contractRent} />,
        dataType: 'number',
        sortable: true,
        searchable: true,
      },
      {
        id: 'utilityAllowance',
        label: <FormattedMessage {...messages.utilityAllowance} />,
        dataType: 'number',
        sortable: true,
        searchable: true,
      },
      {
        id: 'grossRent',
        label: <FormattedMessage {...messages.grossRent} />,
        dataType: 'number',
        sortable: true,
        searchable: true,
      },
      {
        id: 'incomeCode',
        label: <FormattedMessage {...messages.incomeCode} />,
        dataType: 'string',
        multiselect: true,
        sortable: true,
        searchable: true,
        filterOptions: filterOptions.incomeCode,
      },
      {
        id: 'turnoverCode',
        label: <FormattedMessage {...messages.turnoverCode} />,
        dataType: 'string',
        sortable: true,
        searchable: true,
        multiselect: true,
        filterOptions: filterOptions.turnoverCode,
      },
      {
        id: 'turnoverMonthDay',
        label: <FormattedMessage {...messages.turnoverMonthDay} />,
        dataType: 'date',
        sortable: true,
        searchable: true,
      },
      {
        id: 'nextRecertDate',
        label: <FormattedMessage {...messages.nextRecertDate} />,
        dataType: 'date',
        sortable: true,
        searchable: true,
      },
      {
        id: 'changeCode',
        label: <FormattedMessage {...messages.changeCode} />,
        dataType: 'string',
        sortable: true,
        searchable: true,
        multiselect: true,
        filterOptions: filterOptions.changeCode,
      },
      {
        id: 'requestedAmount',
        label: <FormattedMessage {...messages.requestedAmount} />,
        dataType: 'number',
        sortable: true,
        searchable: true,
      },
      {
        id: 'approvedAmount',
        label: <FormattedMessage {...messages.approvedAmount} />,
        dataType: 'number',
        defaultWidth: DEFAULT_APPROVED_AMOUNT_COLUMN_WIDTH,
        sortable: true,
        searchable: true,
      },
    ],
    [filterOptions],
  );
};

export default useHeaders;
